import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { assessmentRbacChecker } from "../../../../../utils/rbac";
import { getdebridementAsync, patchDebridementAsync } from "../../../../../app/patientSlice";
import { useReadOnly } from "../../../../../hooks/useReadOnly";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { getCareSetting } from "../debridement/debridementConfig";
import {
  visiblePrerequisites,
  complianceFlags,
  readinessCount,
  missingReasons,
  requiresApproach,
  showsSubcutaneousSubList,
  stateFromResponse,
} from "../debridement/debridementRules";
import { buildDebridementPayload } from "../debridement/debridementPayload";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";

/**
 * Debridement — section hook.
 *
 * Differs from the previous implementation in two ways:
 *
 * 1. NO OPTION FETCHING. Questions and answers come from debridementConfig.js,
 *    bundled with the client, so the section paints and functions offline.
 *    The ten `optionTypeAsync` dispatches are gone.
 *
 * 2. NO DECISION LOGIC HERE. Visibility, policy and compliance live in
 *    debridementRules.js as pure functions. This hook owns state and wiring
 *    only — which is what makes the rules unit-testable.
 */
export const useDebridement = ({ interventionId, formularyId }) => {
  const { control, register, handleSubmit, reset, errors } = useForm();
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();
  const isReadOnly = useReadOnly();

  const submitLoading = useLoading(LOADING_KEYS.UPDATE_DEBRIDEMENT);
  const viewLoading = useLoading(LOADING_KEYS.GET_DEBRIDEMENT);

  const resp = useSelector((state) => state.patient.debridement);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const userDetails = useSelector((state) => state.user);
  const { hasPermission } = usePermissions();

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [readyOpen, setReadyOpen] = useState(true);
  const [showReasonErrors, setShowReasonErrors] = useState(false);
  const [form, setForm] = useState(() => stateFromResponse(null));

  const instrumentsUsedRef = useRef(null);
  const necroticTissueRef = useRef(null);

  /* Care setting driveformularyIds which prerequisites render. Sourced from
     detailViewData.customerGroup — see the fit-analysis note: this field must
     be added to the NE1ConfigDetails response. getCareSetting() falls back to
     the most restrictive setting so a missing value never hides a question. */
  const careSetting = useMemo(() => getCareSetting(patientData), [patientData]);

  const hideButtons = assessmentRbacChecker(userDetails?.role, patientData?.isOwnCustomer);

  useEffect(() => {
    if (patientId && assessmentId) {
      dispatch(getdebridementAsync({ patientId, assessmentId, interventionId }));
    }
  }, [dispatch, patientId, assessmentId, interventionId]);

  useEffect(() => {
    setForm(stateFromResponse(resp));
    resp === null ? setEditable(true) : setEditable(false);
  }, [resp]);

  /* ── derived ─────────────────────────────────────────────────────── */

  const prerequisites = useMemo(() => visiblePrerequisites(careSetting), [careSetting]);

  const flags = useMemo(() => complianceFlags(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const readiness = useMemo(() => readinessCount(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const reasonGaps = useMemo(() => missingReasons(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const performed = form.isDebridementPerformed === "yes";
  const showApproach = performed && requiresApproach(form.surgicalDebridementProcedure);
  const showSubcutaneous = performed && showsSubcutaneousSubList(form.depthOfDebridement);
  const showPressure = performed && Boolean(form.postDebridementBleedingAmount) && form.postDebridementBleedingAmount !== "none";

  /* ── field setters ───────────────────────────────────────────────── */

  const setField = useCallback((name, value) => {
    setFormChangesConfirm(true);
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      // Changing the type invalidates an approach chosen under the old one.
      if (name === "surgicalDebridementProcedure") next.nonExcisionalApproach = "";

      // The subcutaneous sub-list only exists under one depth value.
      if (name === "depthOfDebridement" && !showsSubcutaneousSubList(value)) {
        next.necroticSubcutaneousTissue = [];
      }

      // "Pressure required" is meaningless with no bleeding.
      if (name === "postDebridementBleedingAmount" && (!value || value === "none")) {
        next.stoppedBleedingContactPressure = "";
      }

      return next;
    });
    // if (name === "isDebridementPerformed" && value === "no") {
    //   reset();
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMulti = useCallback((name, key) => {
    setFormChangesConfirm(true);
    setForm((prev) => {
      const list = Array.isArray(prev[name]) ? prev[name] : [];
      return {
        ...prev,
        [name]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key],
      };
    });
  }, []);

  const handleMultiSelect = useCallback((name, options, ref) => {
    setFormChangesConfirm(true);

    const selectedKeys = Array.isArray(options) ? options.map((item) => item.key) : [];

    setForm((prev) => ({
      ...prev,
      [name]: selectedKeys,
    }));
  }, []);

  /* ── prerequisite answers ────────────────────────────────────────── */

  const setPrerequisiteAnswer = useCallback((key, value) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: {
          ...(prev.prerequisiteAnswers?.[key] || {}),
          value,
          // clear a stale reason when moving off "no"
          ...(value === "no" ? {} : { reasonCode: null, reasonText: null }),
        },
      },
    }));
  }, []);

  const setPrerequisiteSelection = useCallback((key, selection) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: {
          ...(prev.prerequisiteAnswers?.[key] || {}),
          selection,
          // picking a value implies the prerequisite is satisfied
          value: prev.prerequisiteAnswers?.[key]?.value || (selection ? "yes" : null),
        },
      },
    }));
  }, []);

  const setPrerequisiteReason = useCallback((key, reasonCode, reasonText) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: { ...(prev.prerequisiteAnswers?.[key] || {}), value: "no", reasonCode, reasonText },
      },
    }));
  }, []);

  /* ── submit ──────────────────────────────────────────────────────── */

  const doSubmit = useCallback(() => {
    /* A "no" needs a reason before it can be persisted — that is a
       completeness check on the FORM, not a compliance gate. Flags themselves
       never prevent a save; see debridementRules.complianceFlags. */
    if (reasonGaps.length > 0) {
      setShowReasonErrors(true);
      setReadyOpen(true);
      return;
    }

    const payload = {
      state: form,
      careSetting,
      ids: { patientId, assessmentId, interventionId },
    };
    const finalPayload = buildDebridementPayload(payload);

    dispatch(patchDebridementAsync(finalPayload, payload?.ids, setEditable));

    reset();
    setShowReasonErrors(false);
    // setEditable(false);
    setFormChangesConfirm(false);
  }, [dispatch, reset, form, careSetting, reasonGaps.length, patientId, assessmentId, interventionId]);

  /* ── edit / cancel ───────────────────────────────────────────────── */

  const editMode = useCallback(() => {
    setForm(stateFromResponse(resp));
    setEditable(true);
    setReadyOpen(true);
    setShowReasonErrors(false);
    setFormChangesConfirm(false);
  }, [resp]);

  const handleCancelClick = useCallback(() => {
    if (formChangesConfirm) setConfirmationModal(true);
    else setEditable(false);
  }, [formChangesConfirm]);

  const confirmationClose = useCallback(() => setConfirmationModal(false), []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setForm(stateFromResponse(resp));
  }, [resp]);

  return {
    // form plumbing
    control,
    register,
    handleSubmit,
    errors,
    doSubmit,
    submitLoading,
    viewLoading,
    // data
    resp,
    form,
    careSetting,
    prerequisites,
    flags,
    readiness,
    reasonGaps,
    showReasonErrors,
    // visibility
    performed,
    showApproach,
    showSubcutaneous,
    showPressure,
    readyOpen,
    setReadyOpen,
    // mutation
    setField,
    toggleMulti,
    setPrerequisiteAnswer,
    setPrerequisiteSelection,
    setPrerequisiteReason,
    // chrome
    editable,
    editMode,
    isReadOnly,
    hideButtons,
    noDataMsg: !resp,
    formularyId,
    setFormChangesConfirm,
    confirmationModal,
    confirmationClose,
    yesConfirmationModalClick,
    handleCancelClick,
    instrumentsUsedRef,
    necroticTissueRef,
    handleMultiSelect,
    hasPermission,
  };
};
